/**
 * BHCS calendar of days a program does *not* run.
 *
 * Source of truth: https://bhcskids.com/calendar plus the Fall 2026 enrichment schedule
 * sheet (transcribed 2026-07-09, extended 2026-09-05). The public calendar is a custom JS
 * month-view with no export feed, so this is a hand-maintained static mirror — update it
 * whenever the school calendar changes. Intentionally school-agnostic: a BHCS closure or
 * camp applies to the program, not to any one partner school (unlike the legacy,
 * over-partitioned `school_holidays` table, which this replaces here).
 *
 * ## Two audiences, three kinds
 *
 * After-school and enrichment do not share a calendar, and the difference is not noise —
 * it is most of the entries. A camp week closes after-school while the camps themselves
 * run; a *morning* camp leaves after-school open while every enrichment vendor cancels.
 * So a day carries which audience it stops, and callers ask the question they actually
 * mean:
 *
 * | After-school | Enrichment | `kind`          | e.g.                                        |
 * |--------------|------------|-----------------|---------------------------------------------|
 * | runs         | runs       | *(not listed)*  | an ordinary school day                      |
 * | off          | off        | `closed`        | Thanksgiving, Christmas, summer/Winter Camp |
 * | runs         | off        | `no_enrichment` | Fall Camp week, Halloween, 9/21             |
 *
 * ## Which accessor to use
 *
 * `isBlackoutDate` / `blackoutDateSet` / `getBlackoutLabel` answer **"is after-school
 * off?"** — `closed` days, and nothing else. They are what the Flex plan
 * editor and the monthly plan save path consume to decide which days are billable, so a
 * `no_enrichment` day leaking into them would stop selling after-school on a day it
 * genuinely runs. That is the one way this file can cost money, and the split is what
 * prevents it: adding a `no_enrichment` row cannot change what those three return.
 *
 * `isEnrichmentClosed` / `getEnrichmentClosureLabel` answer **"is enrichment off?"** —
 * both kinds, since a `closed` day stops everything. Camps ask neither; they run on the very days that close
 * after-school (see `isSessionDate`).
 *
 * `date` is a business-TZ calendar day (YYYY-MM-DD); weekend entries would be harmless but
 * weekday flex never offers them.
 *
 * ## Two brands, two independent calendars
 *
 * Every accessor takes a brand, defaulting to `DEFAULT_BRAND_KEY`, so the ~35 existing call
 * sites are unchanged. The calendars do not overlay: a brand with no rows is **open**, not
 * "same as BHCS". `FALLBACK_CLOSURES` below is BHCS's calendar and stays BHCS-only —
 * a merged fallback would suppress billable Kaleido days whenever the database read failed.
 */

import { DEFAULT_BRAND_KEY, type BrandKey } from './brands';

/**
 * Who a day stops.
 *
 * - `closed` — neither after-school nor enrichment runs. Covers both a shut building
 *   (Thanksgiving, Christmas) and a camp week that displaces after-school: enrichment
 *   classes meet *inside* the after-school block, so with no block there is nothing for
 *   them to sit in. There was once a third kind, `no_afterschool`, for the camp case; it
 *   never had a single live example and its only effect was to let enrichment claim a
 *   session on a day after-school was not running.
 * - `no_enrichment` — after-school runs normally and stays billable; enrichment is off.
 *
 * Camps consult neither: they run on the very days that close after-school, so
 * `isClosedForProgramType` exempts them before it reads this at all.
 */
export type BlackoutKind = 'closed' | 'no_enrichment';

export interface BlackoutDate {
  /** YYYY-MM-DD, America/Los_Angeles calendar day. */
  date: string;
  /** Short reason shown on the disabled day, e.g. "BHCS Closed", "Summer Camp". */
  label: string;
  /** Which audience this day stops. See `BlackoutKind`. */
  kind: BlackoutKind;
  /**
   * Whose calendar this day belongs to. Optional because the compiled-in fallback and every
   * fixture predating brands omit it; absent reads as `DEFAULT_BRAND_KEY`, matching the
   * column default.
   *
   * The calendars are **fully independent**, not variations on one another: Kaleido can run
   * on a day BHCS is shut, and can meet on Saturday. That is why this is a separate calendar
   * rather than an exception list layered over the BHCS one.
   */
  brand_key?: BrandKey;
}

const FALLBACK_CLOSURES: readonly BlackoutDate[] = [
  // ── July 2026 — summer camp weeks (no after-school) + Independence Day ───────────────────
  { date: '2026-07-01', label: 'Summer Camp', kind: 'closed' },
  { date: '2026-07-02', label: 'Summer Camp', kind: 'closed' },
  { date: '2026-07-03', label: 'BHCS Closed — Independence Day', kind: 'closed' },
  { date: '2026-07-06', label: 'Summer Camp', kind: 'closed' },
  { date: '2026-07-07', label: 'Summer Camp', kind: 'closed' },
  { date: '2026-07-08', label: 'Summer Camp', kind: 'closed' },
  { date: '2026-07-09', label: 'Summer Camp', kind: 'closed' },
  { date: '2026-07-10', label: 'Summer Camp', kind: 'closed' },
  { date: '2026-07-13', label: 'Summer Camp', kind: 'closed' },
  { date: '2026-07-14', label: 'Summer Camp', kind: 'closed' },
  { date: '2026-07-15', label: 'Summer Camp', kind: 'closed' },
  { date: '2026-07-16', label: 'Summer Camp', kind: 'closed' },
  { date: '2026-07-17', label: 'Summer Camp', kind: 'closed' },
  { date: '2026-07-20', label: 'Summer Camp', kind: 'closed' },
  { date: '2026-07-21', label: 'Summer Camp', kind: 'closed' },
  { date: '2026-07-22', label: 'Summer Camp', kind: 'closed' },
  { date: '2026-07-23', label: 'Summer Camp', kind: 'closed' },
  { date: '2026-07-24', label: 'Summer Camp', kind: 'closed' },
  { date: '2026-07-27', label: 'Summer Camp', kind: 'closed' },
  { date: '2026-07-28', label: 'Summer Camp', kind: 'closed' },
  { date: '2026-07-29', label: 'Summer Camp', kind: 'closed' },
  { date: '2026-07-30', label: 'Summer Camp', kind: 'closed' },
  { date: '2026-07-31', label: 'Summer Camp', kind: 'closed' },

  // ── August 2026 — summer camp week 8 (no after-school); Aug 11 = first day of school ─────
  { date: '2026-08-03', label: 'Summer Camp', kind: 'closed' },
  { date: '2026-08-04', label: 'Summer Camp', kind: 'closed' },
  { date: '2026-08-05', label: 'Summer Camp', kind: 'closed' },
  { date: '2026-08-06', label: 'Summer Camp', kind: 'closed' },
  { date: '2026-08-07', label: 'Summer Camp', kind: 'closed' },

  // ── September 2026 ──────────────────────────────────────────────────────────────────────
  { date: '2026-09-07', label: 'BHCS Closed — Labor Day', kind: 'closed' },
  // An *opt-in* full-day camp: children who did not enrol simply do not attend, and
  // after-school runs as usual — so this is emphatically not a closure, and Flex families
  // may still buy the day. Enrichment is cancelled school-wide. The vendor sheets omit it
  // by convention (it is assumed known), which is exactly how it stayed invisible.
  { date: '2026-09-21', label: 'Mid-Autumn Day Camp — no enrichment', kind: 'no_enrichment' },

  // ── October 2026 — Fall Camp runs mornings, so after-school is open all week and only
  //    enrichment stops. Halloween is a themed day, same shape.
  { date: '2026-10-05', label: 'Fall Camp — no enrichment', kind: 'no_enrichment' },
  { date: '2026-10-06', label: 'Fall Camp — no enrichment', kind: 'no_enrichment' },
  { date: '2026-10-07', label: 'Fall Camp — no enrichment', kind: 'no_enrichment' },
  { date: '2026-10-08', label: 'Fall Camp — no enrichment', kind: 'no_enrichment' },
  { date: '2026-10-09', label: 'Fall Camp — no enrichment', kind: 'no_enrichment' },
  { date: '2026-10-30', label: 'Halloween — no enrichment', kind: 'no_enrichment' },

  // ── November 2026 — Nov 23–25 is "Morning Camp + After-school": after-school stays
  //    selectable and billable, enrichment does not run.
  { date: '2026-11-11', label: 'BHCS Closed — Veterans Day', kind: 'closed' },
  { date: '2026-11-23', label: 'Morning Camp — no enrichment', kind: 'no_enrichment' },
  { date: '2026-11-24', label: 'Morning Camp — no enrichment', kind: 'no_enrichment' },
  { date: '2026-11-25', label: 'Morning Camp — no enrichment', kind: 'no_enrichment' },
  { date: '2026-11-26', label: 'BHCS Closed — Thanksgiving', kind: 'closed' },
  { date: '2026-11-27', label: 'BHCS Closed — Thanksgiving', kind: 'closed' },

  // ── December 2026 — Dec 17–18 "Morning Camp + After-school" stay selectable ──────────────
  { date: '2026-12-21', label: 'Winter Camp', kind: 'closed' },
  { date: '2026-12-22', label: 'Winter Camp', kind: 'closed' },
  { date: '2026-12-23', label: 'Winter Camp', kind: 'closed' },
  { date: '2026-12-24', label: 'BHCS Closed', kind: 'closed' },
  { date: '2026-12-25', label: 'BHCS Closed — Christmas', kind: 'closed' },
  { date: '2026-12-28', label: 'BHCS Closed', kind: 'closed' },
  { date: '2026-12-29', label: 'BHCS Closed', kind: 'closed' },
  { date: '2026-12-30', label: 'BHCS Closed', kind: 'closed' },
  // Dec 31 has no closed marker on the calendar grid, but the "Holiday Closures" list is
  // correct — school is closed (confirmed 2026-08-25).
  { date: '2026-12-31', label: "BHCS Closed — New Year's Eve", kind: 'closed' },
  // TODO(calendar): extend to Jan–Jun 2027 (New Year, MLK 1/18, Presidents' 2/15,
  // Memorial 5/31, spring camps) from https://bhcskids.com/calendar when scheduling those.
  // Collect the enrichment no-class dates in the same pass — the vendor sheets omit
  // school-wide days, so a verbatim import inherits the 9/21 hole.
];

/**
 * The calendar in force right now.
 *
 * Starts as `FALLBACK_CLOSURES` — compiled into the bundle — and is replaced by
 * `primeClosures` once the `program_closures` table has been read. The accessors below stay
 * **synchronous** so that every existing caller is unchanged, in particular the Flex plan
 * editor and its save path, which decide what a family may be billed for.
 *
 * That is the whole reason the fallback is compiled in rather than fetched. If these were
 * async and the fetch failed, they would report "nothing is closed" and Christmas would
 * become a sellable, billable after-school day. Worst case here is last-deploy's calendar,
 * never an open one.
 */
let activeClosures: readonly BlackoutDate[] = FALLBACK_CLOSURES;

/** One brand's pair of maps. Both are always present, both may be empty. */
interface BrandCalendar {
  /** Days after-school does not run: `closed` only. A `no_enrichment` day is billable. */
  afterSchool: ReadonlyMap<string, string>;
  /** Days enrichment does not run: both kinds, since `closed` stops everything. */
  enrichment: ReadonlyMap<string, string>;
}

const EMPTY_CALENDAR: BrandCalendar = { afterSchool: new Map(), enrichment: new Map() };

function buildMap(rows: readonly BlackoutDate[], keep: (kind: BlackoutKind) => boolean): Map<string, string> {
  return new Map(rows.filter((b) => keep(b.kind)).map((b) => [b.date, b.label]));
}

/**
 * Partition rows into one calendar per brand.
 *
 * A row with no `brand_key` is BHCS's — that is the column default, and it is what every
 * compiled-in fallback entry and every pre-brand fixture means.
 */
function buildCalendars(rows: readonly BlackoutDate[]): Map<BrandKey, BrandCalendar> {
  const byBrand = new Map<BrandKey, BlackoutDate[]>();
  for (const row of rows) {
    const brand = row.brand_key ?? DEFAULT_BRAND_KEY;
    const bucket = byBrand.get(brand);
    if (bucket) bucket.push(row);
    else byBrand.set(brand, [row]);
  }
  const out = new Map<BrandKey, BrandCalendar>();
  for (const [brand, brandRows] of byBrand) {
    out.set(brand, {
      afterSchool: buildMap(brandRows, (k) => k === 'closed'),
      enrichment: buildMap(brandRows, () => true),
    });
  }
  return out;
}

let CALENDARS: ReadonlyMap<BrandKey, BrandCalendar> = buildCalendars(activeClosures);

/**
 * The calendar in force for `brand`.
 *
 * A brand absent from the map is **open**, not "same as BHCS" — the calendars are
 * independent, so falling back to another brand's closures would cancel days that brand
 * genuinely runs. On day one a new brand legitimately has no rows at all.
 */
function calendarFor(brand: BrandKey): BrandCalendar {
  return CALENDARS.get(brand) ?? EMPTY_CALENDAR;
}

/**
 * Replace the in-force calendar with rows read from `program_closures`.
 *
 * Called once by `useClosures` when the query resolves. Idempotent, and safe to call again
 * after an admin edits a day.
 *
 * **An empty array is ignored.** PostgREST answers `[]` with HTTP 200 when RLS filters every
 * row, which is indistinguishable from a genuinely empty table — and treating that as "no
 * closures" is exactly the failure that would sell Christmas. There is always at least one
 * holiday in a school year, so zero rows is read as a failed load and the previous calendar
 * is kept. The corollary is that deleting *every* closure is not a supported state; delete
 * them down to one.
 *
 * **The guard is global, and the partition by brand happens after it passes.** Guarding each
 * brand separately would read a new brand's legitimately-empty calendar as a failed load and
 * substitute stale rows for it — and on day one a new brand always has none. Guarding the
 * whole fetch is sound because the threat model is RLS filtering the read, and
 * `program_closures`' SELECT policy is `USING (true)` with no brand predicate: the failure is
 * all-or-nothing, with no partial case where one brand's rows vanish and another's survive.
 * **If that policy ever becomes brand-aware, this has to be revisited.**
 *
 * Returns whether the calendar was actually replaced, so the caller can log a skipped prime.
 */
export function primeClosures(rows: readonly BlackoutDate[]): boolean {
  if (!rows || rows.length === 0) return false;
  activeClosures = rows;
  CALENDARS = buildCalendars(rows);
  return true;
}

/** Reset to the compiled-in calendar. Tests only — production primes forward, never back. */
export function resetClosuresToFallback(): void {
  activeClosures = FALLBACK_CLOSURES;
  CALENDARS = buildCalendars(activeClosures);
}

/**
 * Every closure currently in force, whatever its kind.
 *
 * The admin editor and the seed-parity test read this; day-to-day callers want one of the
 * two audience-scoped accessors below instead.
 */
export function allClosures(brand?: BrandKey): readonly BlackoutDate[] {
  if (!brand) return activeClosures;
  return activeClosures.filter((c) => (c.brand_key ?? DEFAULT_BRAND_KEY) === brand);
}

/** Which brands currently have at least one closure. Admin calendar UI and tests. */
export function closureBrands(): readonly BrandKey[] {
  return [...CALENDARS.keys()];
}

/**
 * The compiled-in calendar, used to seed `program_closures` and as the fallback.
 *
 * Kept exported under its original name because the acceptance suite asserts against it: it
 * is the calendar those twelve vendor session counts were derived from, and a DB edit must
 * not silently change what that test is proving.
 */
export { FALLBACK_CLOSURES as BHCS_BLACKOUT_DATES, FALLBACK_CLOSURES };

/**
 * True when `ymd` (YYYY-MM-DD) is a day **after-school** does not run.
 *
 * This is the billing question — it decides which days a Flex plan may sell — so it
 * deliberately ignores `no_enrichment` days, on which after-school runs normally.
 */
export function isBlackoutDate(ymd: string, brand: BrandKey = DEFAULT_BRAND_KEY): boolean {
  return calendarFor(brand).afterSchool.has(ymd);
}

/** The reason after-school is off that day, or undefined when it isn't. */
export function getBlackoutLabel(ymd: string, brand: BrandKey = DEFAULT_BRAND_KEY): string | undefined {
  return calendarFor(brand).afterSchool.get(ymd);
}

/** Every day after-school is off, as a set (for bulk filtering, e.g. trimming a plan). */
export function blackoutDateSet(brand: BrandKey = DEFAULT_BRAND_KEY): ReadonlySet<string> {
  return new Set(calendarFor(brand).afterSchool.keys());
}

/**
 * True when `ymd` (YYYY-MM-DD) is a day **enrichment classes** do not run.
 *
 * Covers a full BHCS closure and the camp/themed days that cancel enrichment while
 * after-school carries on. A vendor that additionally takes its own day off is not here —
 * that lives in `programs.excluded_dates`, per program.
 */
export function isEnrichmentClosed(ymd: string, brand: BrandKey = DEFAULT_BRAND_KEY): boolean {
  return calendarFor(brand).enrichment.has(ymd);
}

/** The reason enrichment is off that day, or undefined when it isn't. */
export function getEnrichmentClosureLabel(
  ymd: string,
  brand: BrandKey = DEFAULT_BRAND_KEY,
): string | undefined {
  return calendarFor(brand).enrichment.get(ymd);
}
